import type {
  ApiSharedDrive,
  ApiSharedDriveMember,
  SharedDriveMemberResponse,
  SharedDriveMembersResponse,
  SharedDriveRole,
  UpsertSharedDriveMemberRequest,
} from "@workith/shared";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
} from "@workith/ui";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import api from "../../lib/api";

const ROLES: SharedDriveRole[] = ["manager", "editor", "viewer"];

function reportError(message: string, error: unknown) {
  console.error(message, error);
  alert(error instanceof Error ? error.message : message);
}

interface SharedDriveMembersDialogProps {
  drive: ApiSharedDrive;
  children: ReactNode;
}

export function SharedDriveMembersDialog({ drive, children }: SharedDriveMembersDialogProps) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState<ApiSharedDriveMember[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<SharedDriveRole>("viewer");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    let isActive = true;
    void api
      .get<SharedDriveMembersResponse>(`/api/shared-drives/${drive.id}/members`)
      .then((response) => {
        if (isActive) {
          setMembers(response.members);
        }
      })
      .catch((error) => {
        if (isActive) {
          reportError("Failed to fetch shared drive members", error);
        }
      });

    return () => {
      isActive = false;
    };
  }, [drive.id, open]);

  const addMember = async () => {
    if (!email.trim()) {
      return;
    }

    setSaving(true);
    try {
      const response = await api.post<UpsertSharedDriveMemberRequest, SharedDriveMemberResponse>(
        `/api/shared-drives/${drive.id}/members`,
        { email: email.trim(), role }
      );
      setMembers((currentMembers) => [
        response.member,
        ...currentMembers.filter((member) => member.iam_user_id !== response.member.iam_user_id),
      ]);
      setEmail("");
      setRole("viewer");
    } catch (error) {
      reportError("Failed to add shared drive member", error);
    } finally {
      setSaving(false);
    }
  };

  const updateRole = async (member: ApiSharedDriveMember, nextRole: SharedDriveRole) => {
    try {
      const response = await api.put<UpsertSharedDriveMemberRequest, SharedDriveMemberResponse>(
        `/api/shared-drives/${drive.id}/members/${member.iam_user_id}`,
        { role: nextRole }
      );
      setMembers((currentMembers) =>
        currentMembers.map((currentMember) =>
          currentMember.iam_user_id === response.member.iam_user_id
            ? response.member
            : currentMember
        )
      );
    } catch (error) {
      reportError("Failed to update shared drive member", error);
    }
  };

  const removeMember = async (member: ApiSharedDriveMember) => {
    try {
      await api.delete(`/api/shared-drives/${drive.id}/members/${member.iam_user_id}`);
      setMembers((currentMembers) =>
        currentMembers.filter((currentMember) => currentMember.iam_user_id !== member.iam_user_id)
      );
    } catch (error) {
      reportError("Failed to remove shared drive member", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{drive.name} 멤버</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="user@workith.com"
            />
            <select
              value={role}
              onChange={(event) => setRole(event.target.value as SharedDriveRole)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              {ROLES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            <Button type="button" onClick={addMember} disabled={!email.trim() || saving}>
              추가
            </Button>
          </div>
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {members.map((member) => (
              <div
                key={member.iam_user_id}
                className="flex items-center justify-between rounded-md border p-3"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {member.display_name || member.email}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{member.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge>{member.role}</Badge>
                  <select
                    value={member.role}
                    onChange={(event) => updateRole(member, event.target.value as SharedDriveRole)}
                    className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                  >
                    {ROLES.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeMember(member)}
                  >
                    제거
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" onClick={() => setOpen(false)}>
            닫기
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
